import { Button, Checkbox, Popover } from '@ntconsult/aluma-react';
import { Columns3 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnsState } from '../hooks/createColumnsHook';

type ColumnsPickerProps<C extends string> = {
  columnsState: ColumnsState<C>;
  /** Rótulo já traduzido de cada coluna. */
  label: (column: C) => string;
  /** Colunas que não se aplicam à aba atual ficam fora da lista. */
  available?: (column: C) => boolean;
};

/** Escolha de colunas com rascunho: só comita no "Aplicar"; fechar de outro jeito descarta. */
export function ColumnsPicker<C extends string>({
  columnsState,
  label,
  available = () => true,
}: ColumnsPickerProps<C>) {
  const { t } = useTranslation(['participantes', 'common']);
  const { columns, defaultColumns, visibleColumns, setVisibleColumns } = columnsState;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<C[]>(visibleColumns);

  function apply(next: readonly C[]) {
    setVisibleColumns([...next]);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(visibleColumns);
        setOpen(next);
      }}
      placement="bottom-end"
      trigger={
        <Button variant="secondary" size="sm" leftIcon={<Columns3 size={14} />}>
          {t('participantes:colunas.titulo')}
        </Button>
      }
    >
      <form
        className="flex w-56 flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (draft.length > 0) apply(draft);
        }}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium text-foreground">
            {t('participantes:colunas.visiveis')}
          </legend>
          {columns.filter(available).map((column) => (
            <Checkbox
              key={column}
              label={label(column)}
              checked={draft.includes(column)}
              onCheckedChange={(checked) =>
                setDraft((cur) => (checked ? [...cur, column] : cur.filter((c) => c !== column)))
              }
            />
          ))}
        </fieldset>
        {draft.length === 0 && (
          <p className="text-xs text-foreground-muted">{t('participantes:colunas.nenhuma')}</p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => apply(defaultColumns)}>
            {t('participantes:colunas.redefinir')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
            {t('common:cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={draft.length === 0}>
            {t('participantes:colunas.aplicar')}
          </Button>
        </div>
      </form>
    </Popover>
  );
}
